import { EventsApiHelpers } from "../../../general/apiHelpers/eventsApiHelpers";
import type { AftermathApi } from "../../../general/providers/aftermathApi";
import type {
	MoveErrors,
	MoveErrorsInterface,
} from "../../../general/types/moveErrorsInterface";
import type {
	AnyObjectType,
	CoinType,
	PerpetualsAddresses,
} from "../../../types";

export class PerpetualsApi implements MoveErrorsInterface {
	// =========================================================================
	//  Class Members
	// =========================================================================

	private static readonly constants = {
		moduleNames: {
			interface: "interface",
			orderbook: "orderbook",
			events: "events",
			clearingHouse: "clearing_house",
			account: "account",
		},
	};

	public readonly addresses: PerpetualsAddresses;

	public readonly eventTypes: {
		withdrewCollateral: AnyObjectType;
		depositedCollateral: AnyObjectType;
		settledFunding: AnyObjectType;
		allocatedCollateral: AnyObjectType;
		deallocatedCollateral: AnyObjectType;
		liquidated: AnyObjectType;
		createdAccount: AnyObjectType;
		canceledOrder: AnyObjectType;
		postedOrder: AnyObjectType;
		filledMakerOrders: AnyObjectType;
		filledMakerOrder: AnyObjectType;
		filledTakerOrder: AnyObjectType;
		updatedPremiumTwap: AnyObjectType;
		updatedSpreadTwap: AnyObjectType;
		updatedFunding: AnyObjectType;
		updatedMarketVersion: AnyObjectType;
		createdStopOrderTicket: AnyObjectType;
		deletedStopOrderTicket: AnyObjectType;
		editedStopOrderTicketExecutor: AnyObjectType;
		addedStopOrderTicketCollateral: AnyObjectType;
		removedStopOrderTicketCollateral: AnyObjectType;
		editedStopOrderTicketDetails: AnyObjectType;
		executedStopOrderTicket: AnyObjectType;
		filledTakerOrderLiquidator: AnyObjectType;
		performedLiquidation: AnyObjectType;
		reducedOrder: AnyObjectType;
		performedAdl: AnyObjectType;
	};
	public readonly moveErrors: MoveErrors;

	// =========================================================================
	//  Constructor
	// =========================================================================

	constructor(private readonly api: AftermathApi) {
		const addresses = this.api.addresses.perpetuals;
		if (!addresses) {
			throw new Error("not all required addresses have been set in provider");
		}

		this.addresses = addresses;
		this.eventTypes = {
			// Collateral
			withdrewCollateral: this.eventType("WithdrewCollateral"),
			depositedCollateral: this.eventType("DepositedCollateral"),
			settledFunding: this.eventType("SettledFunding"),
			allocatedCollateral: this.eventType("AllocatedCollateral"),
			deallocatedCollateral: this.eventType("DeallocatedCollateral"),
			// Liquidation
			liquidated: this.eventType("LiquidatedPosition"),
			filledTakerOrderLiquidator: this.eventType("FilledTakerOrderLiquidator"),
			performedLiquidation: this.eventType("PerformedLiquidation"),
			// Account
			createdAccount: this.eventType("CreatedAccount"),
			// Order
			canceledOrder: this.eventType("CanceledOrder"),
			filledMakerOrders: this.eventType("FilledMakerOrders"),
			filledMakerOrder: this.eventType("FilledMakerOrder"),
			filledTakerOrder: this.eventType("FilledTakerOrder"),
			reducedOrder: this.eventType("ReducedOrder"),
			postedOrder: this.eventType("PostedOrder"),
			// Twap
			updatedPremiumTwap: this.eventType("UpdatedPremiumTwap"),
			updatedSpreadTwap: this.eventType("UpdatedSpreadTwap"),
			// Funding
			updatedFunding: this.eventType("UpdatedFunding"),
			// Version
			updatedMarketVersion: this.eventType("UpdatedClearingHouseVersion"),
			// Stop Order
			createdStopOrderTicket: this.eventType("CreatedStopOrderTicket"),
			deletedStopOrderTicket: this.eventType("DeletedStopOrderTicket"),
			editedStopOrderTicketExecutor: this.eventType(
				"EditedStopOrderTicketExecutor"
			),
			addedStopOrderTicketCollateral: this.eventType(
				"AddedStopOrderTicketCollateral"
			),
			removedStopOrderTicketCollateral: this.eventType(
				"RemovedStopOrderTicketCollateral"
			),
			editedStopOrderTicketDetails: this.eventType(
				"EditedStopOrderTicketDetails"
			),
			executedStopOrderTicket: this.eventType("ExecutedStopOrderTicket"),
			// ADL
			performedAdl: this.eventType("PerformedADL"),
		};
		this.moveErrors = {
			// TODO: add support for this back in some way ?
			// [this.addresses.packages.perpetuals]: {
			// 	["ANY"]: {
			// 		// ClearingHouse
			// 		/// Cannot deposit/withdraw zero coins to/from the account's collateral.
			// 		0: "Deposit Or Withdraw Amount Zero",
			// 		/// Orderbook size or price are invalid values
			// 		1: "Invalid Size Or Price",
			// 		/// Index price returned from oracle is 0 or invalid value
			// 		2: "Bad Index Price",
			// 		/// Order value in USD is too low
			// 		4: "Order Usd Value Too Low",
			// 		/// Passed a vector of invalid order ids to perform force cancellation during liquidation
			// 		5: "Invalid Force Cancel Ids",
			// 		/// Liquidate must be the first operation of the session, if performed.
			// 		6: "Liquidate Not First Operation",
			// 		/// Passed a vector of invalid order ids to cancel
			// 		7: "Invalid Cancel Order Ids",
			// 		/// Ticket has already passed `expire_timestamp` and can only be cancelled
			// 		8: "Stop Order Ticket Expired",
			// 		/// Index price is not at correct value to satisfy stop order conditions
			// 		9: "Stop Order Conditions Violated",
			// 		/// Index price is not at correct value to satisfy stop order conditions
			// 		10: "Wrong Order Details",
			// 		/// Invalid base price feed storage for the clearing house
			// 		11: "Invalid Base Price Feed Storage",
			// 		/// Same liquidator and liqee account ids
			// 		12: "Self Liquidation",
			// 		/// User trying to access the subaccount is not the one specified by parent
			// 		13: "Invalid Sub Account User",
			// 		/// The parent `Account` trying to delete the subaccount is not the correct one.
			// 		14: "Wrong Parent For Sub Account",
			// 		/// Raised when trying to delete a subaccount still containing collateral.
			// 		15: "Sub Account Contains Collateral",
			// 		/// Raised when trying to call a function with the wrong package's version
			// 		16: "Wrong Version",
			// 		/// Raised when trying to have a session composed by only `start_session` and `end_session`
			// 		17: "Empty Session",
			// 		/// Market already registered in the registry
			// 		18: "Market Already Registered",
			// 		/// Collateral is not registered in the registry
			// 		19: "Collateral Is Not Registered",
			// 		/// Market is not registered in the registry
			// 		20: "Market Is Not Registered",
			// 		/// Invalid collateral price feed storage for the clearing house
			// 		21: "Invalid Collateral Price Feed Storage",
			// 		/// Fees accrued are negative
			// 		22: "Negative Fees Accrued",
			// 		/// Reduce-only conditions are not respected for stop order execution
			// 		23: "Not Reduce Only Stop Order",
			// 		/// Stop order gas cost provided is not enough
			// 		24: "Not Enough Gas For Stop Order",
			// 		/// Stop order collateral to allocate provided is not enough
			// 		25: "Not Enough Collateral To Allocate For Stop Order",
			// 		/// Invalid account trying to perform an action on a StopOrderTicket
			// 		26: "Invalid Account For Stop Order",
			// 		/// Invalid executor trying to execute the StopOrderTicket
			// 		27: "Invalid Executor For Stop Order",
			// 		/// Raised when the market's max open interest is surpassed as a result of the session's actions
			// 		28: "Max Open Interest Surpassed",
			// 		/// Raised when a position would get a base amount higher than the allowed percentage of open interest
			// 		29: "Max Open Interest Position Percent Surpassed",
			// 		// Market
			// 		/// While creating ordered map with invalid parameters, or changing them improperly for an existent map.
			// 		1000: "Invalid Market Parameters",
			// 		/// Tried to call `update_funding` before enough time has passed since the last update.
			// 		1001: "Updating Funding Too Early",
			// 		/// Margin-ratio update proposal already exists for market
			// 		1002: "Proposal Already Exists",
			// 		/// Margin-ratio update proposal cannot be committed too early
			// 		1003: "Premature Proposal",
			// 		/// Margin-ratio update proposal delay is outside the valid range
			// 		1004: "Invalid Proposal Delay",
			// 		/// Margin-ratio update proposal does not exist for market
			// 		1005: "Proposal Does Not Exist",
			// 		/// Exchange has no available fees to withdraw
			// 		1006: "No Fees Accrued",
			// 		/// Tried to withdraw more insurance funds than the allowed amount
			// 		1007: "Insufficient Insurance Surplus",
			// 		/// Cannot create a market for which a price feed does not exist
			// 		1008: "No Price Feed For Market",
			// 		/// Cannot delete a proposal that already matured. It can only be committed.
			// 		1009: "Proposal Already Matured",
			// 		// Position
			// 		/// Tried placing a new pending order when the position already has the maximum allowed number of pending orders.
			// 		2000: "Max Pending Orders Exceeded",
			// 		/// Used for checking both liqee and liqor positions during liquidation
			// 		2001: "Position Below IMR",
			// 		///When leaving liqee's position with a margin ratio above tolerance,
			// 		/// meaning that liqor has overbought position
			// 		2002: "Position Above Tolerance",
			// 		/// An operation brought an account below initial margin requirements.
			// 		2003: "Initial Margin Requirement Violated",
			// 		/// Position is above MMR, so can't be liquidated.
			// 		2004: "Position Above MMR",
			// 		/// Cannot realize bad debt via means other than calling 'liquidate'.
			// 		2005: "Position Bad Debt",
			// 		/// Cannot withdraw more than the account's free collateral.
			// 		2006: "Insufficient Free Collateral",
			// 		/// Cannot have more than 1 position in a market.
			// 		2007: "Position Already Exists",
			// 		/// Cannot compute deallocate amount for a target MR < IMR.
			// 		2008: "Deallocate Target MR Too Low",
			// 		// Orderbook & OrderedMap
			// 		/// While creating ordered map with wrong parameters.
			// 		3000: "Invalid Map Parameters",
			// 		/// While searching for a key, but it doesn't exist.
			// 		3001: "Key Not Exist",
			// 		/// While inserting already existing key.
			// 		3002: "Key Already Exists",
			// 		/// When attempting to destroy a non-empty map
			// 		3003: "Destroy Not Empty",
			// 		/// Invalid user tries to modify an order
			// 		3004: "Invalid User For Order",
			// 		/// Orderbook flag requirements violated
			// 		3005: "Flag Requirements Violated",
			// 		/// Minimum size matched not reached
			// 		3006: "Not Enough Liquidity",
			// 		/// When trying to change a map configuration, but the map has length less than 4
			// 		3007: "Map Too Small",
			// 		/// When taker matches its own order
			// 		3008: "Self Trading",
			// 	},
			// },
		};
	}

	// =========================================================================
	//  Objects
	// =========================================================================

	// public fetchMarket = async (inputs: {
	// 	marketId: PerpetualsMarketId;
	// 	collateralCoinType: CoinType;
	// }): Promise<PerpetualsMarketData> => {
	// 	const { collateralCoinType } = inputs;
	// 	return this.api.Objects().fetchCastObject({
	// 		objectId: inputs.marketId,
	// 		objectFromSuiObjectResponse: (data) =>
	// 			Casting.perpetuals.clearingHouseFromOnChain(
	// 				data,
	// 				collateralCoinType
	// 			),
	// 	});
	// };

	// =========================================================================
	//  Inspections
	// =========================================================================

	// public fetchOrderbookPrice = async (inputs: {
	// 	collateralCoinType: ObjectId;
	// 	marketId: PerpetualsMarketId;
	// 	// marketInitialSharedVersion: ObjectVersion;
	// }): Promise<number> => {
	// 	const {
	// 		collateralCoinType,
	// 		marketId,
	// 		// marketInitialSharedVersion
	// 	} = inputs;

	// 	const tx = new Transaction();

	// 	this.getBookPriceTx({
	// 		tx,
	// 		marketId,
	// 		collateralCoinType,
	// 		// marketInitialSharedVersion,
	// 	});

	// 	const bytes =
	// 		await this.api.Inspections().fetchFirstBytesFromTxOutput({
	// 			tx,
	// 		});

	// 	return PerpetualsApiCasting.orderbookPriceFromBytes(bytes);
	// };

	// public fetchAllMarketIds = async (inputs: {
	// 	collateralCoinType: CoinType;
	// }): Promise<PerpetualsMarketId[]> => {
	// 	const { collateralCoinType } = inputs;
	// 	const marketIdsData = await this.api.indexerCaller.fetchIndexer<
	// 		{
	// 			marketId: ObjectId;
	// 		}[]
	// 	>(
	// 		`perpetuals/markets/${Helpers.addLeadingZeroesToType(
	// 			collateralCoinType
	// 		)}`
	// 	);
	// 	return marketIdsData.map((data) =>
	// 		Helpers.addLeadingZeroesToType(data.marketId)
	// 	);
	// };

	// =========================================================================
	//  Transaction Commands
	// =========================================================================

	// public depositCollateralTx = (
	// 	inputs: {
	// 		tx: Transaction;
	// 		collateralCoinType: CoinType;
	// 		accountCapId: ObjectId | TransactionArgument;
	// 	} & (
	// 		| {
	// 				coinId: ObjectId | TransactionArgument;
	// 		  }
	// 		| {
	// 				coinBytes: Uint8Array;
	// 		  }
	// 	)
	// ) => {
	// 	const { tx, collateralCoinType, accountCapId } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			this.addresses.packages.perpetuals,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"deposit_collateral"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			typeof accountCapId === "string"
	// 				? tx.object(accountCapId)
	// 				: accountCapId,
	// 			"coinBytes" in inputs
	// 				? tx.pure(inputs.coinBytes)
	// 				: typeof inputs.coinId === "string"
	// 				? tx.object(inputs.coinId)
	// 				: inputs.coinId,
	// 		],
	// 	});
	// };

	// public allocateCollateralTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	accountCapId: ObjectId | TransactionArgument;
	// 	marketId: PerpetualsMarketId;
	// 	marketInitialSharedVersion: ObjectVersion;
	// 	amount: Balance;
	// }) => {
	// 	const { tx, collateralCoinType, accountCapId, marketId, amount } =
	// 		inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"allocate_collateral"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			tx.sharedObjectRef({
	// 				objectId: marketId,
	// 				initialSharedVersion: inputs.marketInitialSharedVersion,
	// 				mutable: true,
	// 			}),
	// 			typeof accountCapId === "string"
	// 				? tx.object(accountCapId)
	// 				: accountCapId,
	// 			tx.pure.u64(amount),
	// 		],
	// 	});
	// };

	// public deallocateCollateralTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	accountCapId: ObjectId;
	// 	basePriceFeedId: ObjectId;
	// 	collateralPriceFeedId: ObjectId;
	// 	marketId: PerpetualsMarketId;
	// 	marketInitialSharedVersion: ObjectVersion;
	// 	amount: Balance;
	// }) => {
	// 	const { tx, collateralCoinType, accountCapId, marketId, amount } =
	// 		inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"deallocate_collateral"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			tx.sharedObjectRef({
	// 				objectId: marketId,
	// 				initialSharedVersion: inputs.marketInitialSharedVersion,
	// 				mutable: true,
	// 			}),
	// 			tx.object(accountCapId),
	// 			tx.object(inputs.basePriceFeedId),
	// 			tx.object(inputs.collateralPriceFeedId),
	// 			tx.object(Sui.constants.addresses.suiClockId),
	// 			tx.pure.u64(amount),
	// 		],
	// 	});
	// };

	// public createMarketPositionTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	accountCapId: ObjectId | TransactionArgument;
	// 	marketId: PerpetualsMarketId;
	// 	marketInitialSharedVersion: ObjectVersion;
	// }) => {
	// 	const { tx, collateralCoinType, accountCapId, marketId } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"create_market_position"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			tx.sharedObjectRef({
	// 				objectId: marketId,
	// 				initialSharedVersion: inputs.marketInitialSharedVersion,
	// 				mutable: true,
	// 			}),
	// 			typeof accountCapId === "string"
	// 				? tx.object(accountCapId)
	// 				: accountCapId,
	// 		],
	// 	});
	// };

	// public shareClearingHouseTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	marketId: PerpetualsMarketId | TransactionArgument;
	// }) => {
	// 	const { tx, collateralCoinType, marketId } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"share_clearing_house"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			typeof marketId === "string" ? tx.object(marketId) : marketId,
	// 		],
	// 	});
	// };

	// public startSessionTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	accountCapId: ObjectId | TransactionArgument;
	// 	basePriceFeedId: ObjectId;
	// 	collateralPriceFeedId: ObjectId;
	// 	marketId: PerpetualsMarketId;
	// 	marketInitialSharedVersion: ObjectVersion;
	// }) /* SessionHotPotato<T> */ => {
	// 	const { tx, collateralCoinType, accountCapId, marketId } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"start_session"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			tx.sharedObjectRef({
	// 				objectId: marketId,
	// 				initialSharedVersion: inputs.marketInitialSharedVersion,
	// 				mutable: true,
	// 			}),
	// 			typeof accountCapId === "string"
	// 				? tx.object(accountCapId)
	// 				: accountCapId,
	// 			tx.object(inputs.basePriceFeedId),
	// 			tx.object(inputs.collateralPriceFeedId),
	// 			tx.object(Sui.constants.addresses.suiClockId),
	// 		],
	// 	});
	// };

	// public endSessionTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	sessionPotatoId: ObjectId | TransactionArgument;
	// }) /* ClearingHouse<T> */ => {
	// 	const { tx, collateralCoinType, sessionPotatoId } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"end_session"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			typeof sessionPotatoId === "string"
	// 				? tx.object(sessionPotatoId)
	// 				: sessionPotatoId,
	// 		],
	// 	});
	// };

	// public placeMarketOrderTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	sessionPotatoId: ObjectId | TransactionArgument;
	// 	side: PerpetualsOrderSide;
	// 	size: bigint;
	// }) => {
	// 	const { tx, collateralCoinType, sessionPotatoId, side, size } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"place_market_order"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			typeof sessionPotatoId === "string"
	// 				? tx.object(sessionPotatoId)
	// 				: sessionPotatoId,
	// 			tx.pure.bool(Boolean(side)),
	// 			tx.pure.u64(size),
	// 		],
	// 	});
	// };

	// public placeLimitOrderTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	sessionPotatoId: ObjectId | TransactionArgument;
	// 	side: PerpetualsOrderSide;
	// 	size: bigint;
	// 	price: bigint;
	// 	orderType: PerpetualsOrderType;
	// }) => {
	// 	const {
	// 		tx,
	// 		collateralCoinType,
	// 		sessionPotatoId,
	// 		side,
	// 		size,
	// 		price,
	// 		orderType,
	// 	} = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"place_limit_order"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			typeof sessionPotatoId === "string"
	// 				? tx.object(sessionPotatoId)
	// 				: sessionPotatoId,
	// 			tx.pure.bool(Boolean(side)),
	// 			tx.pure.u64(size),
	// 			tx.pure.u64(price),
	// 			tx.pure.u64(BigInt(orderType)),
	// 		],
	// 	});
	// };

	// public cancelOrdersTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	accountCapId: ObjectId;
	// 	marketId: PerpetualsMarketId;
	// 	marketInitialSharedVersion: ObjectVersion;
	// 	orderIds: PerpetualsOrderId[];
	// }) => {
	// 	const { tx, collateralCoinType, accountCapId, marketId, orderIds } =
	// 		inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"cancel_orders"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			tx.sharedObjectRef({
	// 				objectId: marketId,
	// 				initialSharedVersion: inputs.marketInitialSharedVersion,
	// 				mutable: true,
	// 			}),
	// 			tx.object(accountCapId),
	// 			tx.pure(bcs.vector(bcs.u128()).serialize(orderIds)),
	// 		],
	// 	});
	// };

	// public reduceOrdersTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	accountCapId: ObjectId;
	// 	marketId: PerpetualsMarketId;
	// 	marketInitialSharedVersion: ObjectVersion;
	// 	orderIds: PerpetualsOrderId[];
	// 	sizesToSubtract: bigint[];
	// }) => {
	// 	const {
	// 		tx,
	// 		packageId,
	// 		collateralCoinType,
	// 		accountCapId,
	// 		marketId,
	// 		orderIds,
	// 		sizesToSubtract,
	// 	} = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			packageId,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"reduce_orders"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			tx.sharedObjectRef({
	// 				objectId: marketId,
	// 				initialSharedVersion: inputs.marketInitialSharedVersion,
	// 				mutable: true,
	// 			}),
	// 			tx.object(accountCapId),
	// 			tx.pure(bcs.vector(bcs.u128()).serialize(orderIds)),
	// 			tx.pure(bcs.vector(bcs.u64()).serialize(sizesToSubtract)),
	// 		],
	// 	});
	// };

	// public withdrawCollateralTx = (inputs: {
	// 	tx: Transaction;
	// 	collateralCoinType: CoinType;
	// 	accountCapId: ObjectId | TransactionArgument;
	// 	amount: Balance;
	// }): TransactionArgument => {
	// 	const { tx, collateralCoinType, accountCapId, amount } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			this.addresses.packages.perpetuals,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"withdraw_collateral"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			typeof accountCapId === "string"
	// 				? tx.object(accountCapId)
	// 				: accountCapId,
	// 			tx.pure.u64(amount),
	// 		],
	// 	});
	// };

	// public createAccountTx = (inputs: {
	// 	tx: Transaction;
	// 	collateralCoinType: CoinType;
	// }) /* Account<T> */ => {
	// 	const { tx, collateralCoinType } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			this.addresses.packages.perpetuals,
	// 			PerpetualsApi.constants.moduleNames.interface,
	// 			"create_account"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [tx.object(this.addresses.objects.registry)],
	// 	});
	// };

	// public getHotPotatoFieldsTx = (
	// 	inputs: {
	// 		tx: Transaction;
	// 		collateralCoinType: CoinType;
	// 		sessionPotatoId: ObjectId | TransactionArgument;
	// 	}
	// 	/*
	// 		(
	// 			lot_size,
	// 			tick_size,
	// 			timestamp_ms,
	// 			collateral_price,
	// 			index_price,
	// 			book_price,
	// 			fills,
	// 			post
	// 		): (
	// 			u64,
	// 			u64,
	// 			u64,
	// 			u64,
	// 			u256,
	// 			u256,
	// 			u256,
	// 			&vector<FillReceipt>,
	// 			&PostReceipt
	// 		)
	// 	*/
	// ) => {
	// 	const { tx, collateralCoinType, sessionPotatoId } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.clearingHouse,
	// 			"get_hot_potato_fields"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			typeof sessionPotatoId === "string"
	// 				? tx.object(sessionPotatoId)
	// 				: sessionPotatoId,
	// 		],
	// 	});
	// };

	// public getPositionTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	accountId: PerpetualsAccountId;
	// 	marketId: PerpetualsMarketId;
	// 	marketInitialSharedVersion: ObjectVersion;
	// }) /* Position */ => {
	// 	const { tx, marketId, collateralCoinType } = inputs;

	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.clearingHouse,
	// 			"get_position"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			tx.sharedObjectRef({
	// 				objectId: marketId,
	// 				initialSharedVersion: inputs.marketInitialSharedVersion,
	// 				mutable: false,
	// 			}),
	// 			tx.pure.u64(inputs.accountId),
	// 		],
	// 	});
	// };

	// public getOrderbookTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	collateralCoinType: CoinType;
	// 	marketId: PerpetualsMarketId;
	// }) /* Orderbook */ => {
	// 	const { tx, collateralCoinType } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.clearingHouse,
	// 			"get_orderbook"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [tx.object(inputs.marketId)],
	// 	});
	// };

	// public getBookPriceTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	marketId: PerpetualsMarketId;
	// 	// marketInitialSharedVersion: ObjectVersion;
	// 	collateralCoinType: CoinType;
	// }) /* Option<u256> */ => {
	// 	const { tx, marketId, collateralCoinType } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.clearingHouse,
	// 			"get_book_price"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			tx.object(marketId),
	// 			// tx.sharedObjectRef({
	// 			// 	objectId: marketId,
	// 			// 	initialSharedVersion: inputs.marketInitialSharedVersion,
	// 			// 	mutable: false,
	// 			// }),
	// 		],
	// 	});
	// };

	// public getBestPriceTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	marketId: PerpetualsMarketId;
	// 	marketInitialSharedVersion: ObjectVersion;
	// 	side: PerpetualsOrderSide;
	// 	collateralCoinType: CoinType;
	// }) /* Option<u256> */ => {
	// 	const { tx, marketId, collateralCoinType } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.clearingHouse,
	// 			"get_best_price"
	// 		),
	// 		typeArguments: [collateralCoinType],
	// 		arguments: [
	// 			tx.sharedObjectRef({
	// 				objectId: marketId,
	// 				initialSharedVersion: inputs.marketInitialSharedVersion,
	// 				mutable: false,
	// 			}), // ClearingHouse
	// 			tx.pure.bool(Boolean(inputs.side)), // side
	// 		],
	// 	});
	// };

	// public inspectOrdersTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	orderbookId: ObjectId | TransactionArgument;
	// 	side: PerpetualsOrderSide;
	// 	fromPrice: number;
	// 	toPrice: number;
	// }) /* vector<OrderInfo> */ => {
	// 	const { tx, orderbookId } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.orderbook,
	// 			"inspect_orders"
	// 		),
	// 		typeArguments: [],
	// 		arguments: [
	// 			typeof orderbookId === "string"
	// 				? tx.object(orderbookId)
	// 				: orderbookId, // Orderbook
	// 			tx.pure.bool(Boolean(inputs.side)), // side
	// 			tx.pure.u64(inputs.fromPrice), // price_from
	// 			tx.pure.u64(inputs.toPrice), // price_to
	// 		],
	// 	});
	// };

	// public getOrderSizeTx = (inputs: {
	// 	tx: Transaction;
	// 	packageId: PackageId;
	// 	orderbookId: ObjectId | TransactionArgument;
	// 	orderId: PerpetualsOrderId;
	// }) /* u64 */ => {
	// 	const { tx, orderbookId } = inputs;
	// 	return tx.moveCall({
	// 		target: Helpers.transactions.createTxTarget(
	// 			inputs.packageId,
	// 			PerpetualsApi.constants.moduleNames.orderbook,
	// 			"get_order_size"
	// 		),
	// 		typeArguments: [],
	// 		arguments: [
	// 			typeof orderbookId === "string"
	// 				? tx.object(orderbookId)
	// 				: orderbookId, // Orderbook
	// 			tx.pure.u128(inputs.orderId), // order_id
	// 		],
	// 	});
	// };

	// =========================================================================
	//  Transaction Builders
	// =========================================================================

	// public fetchBuildDepositCollateralTx = async (
	// 	inputs: ApiPerpetualsDepositCollateralBody
	// ): Promise<Transaction> => {
	// 	const tx = new Transaction();
	// 	tx.setSender(inputs.walletAddress);

	// 	const { walletAddress, collateralCoinType, amount } = inputs;
	// 	const coinId = await this.api.Coin().fetchCoinWithAmountTx({
	// 		tx,
	// 		walletAddress,
	// 		coinType: collateralCoinType,
	// 		coinAmount: amount,
	// 	});
	// 	this.depositCollateralTx({
	// 		tx,
	// 		coinId,
	// 		...inputs,
	// 	});

	// 	return tx;
	// };

	// public buildCancelOrderTx = (
	// 	inputs: ApiPerpetualsCancelOrderBody
	// ): Transaction => {
	// 	const {
	// 		packageId,
	// 		orderId,
	// 		marketId,
	// 		marketInitialSharedVersion,
	// 		collateralChange,
	// 		basePriceFeedId,
	// 		collateralPriceFeedId,
	// 		...otherInputs
	// 	} = inputs;

	// 	return this.buildCancelOrdersTx({
	// 		...otherInputs,
	// 		orderDatas: [
	// 			{
	// 				packageId,
	// 				orderId,
	// 				marketId,
	// 				marketInitialSharedVersion,
	// 				collateralChange,
	// 				basePriceFeedId,
	// 				collateralPriceFeedId,
	// 			},
	// 		],
	// 	});
	// };

	// public buildCancelOrdersTx = (
	// 	inputs: ApiPerpetualsCancelOrdersBody
	// ): Transaction => {
	// 	const { orderDatas, collateralCoinType, accountCapId } = inputs;

	// 	if (orderDatas.length <= 0)
	// 		throw new Error("cannot have order datas of length zero");

	// 	const tx = new Transaction();
	// 	tx.setSender(inputs.walletAddress);

	// 	const marketIdToOrderIds = orderDatas.reduce(
	// 		(acc, order) => {
	// 			if (order.marketId in acc) {
	// 				return {
	// 					...acc,
	// 					[order.marketId]: [...acc[order.marketId], order],
	// 				};
	// 			}
	// 			return {
	// 				...acc,
	// 				[order.marketId]: [order],
	// 			};
	// 		},
	// 		{} as Record<
	// 			PerpetualsMarketId,
	// 			{
	// 				packageId: PackageId;
	// 				orderId: PerpetualsOrderId;
	// 				marketId: PerpetualsMarketId;
	// 				marketInitialSharedVersion: ObjectVersion;
	// 				collateralChange: Balance;
	// 				basePriceFeedId: ObjectId;
	// 				collateralPriceFeedId: ObjectId;
	// 			}[]
	// 		>
	// 	);

	// 	for (const [marketId, orders] of Object.entries(marketIdToOrderIds)) {
	// 		if (orders.length <= 0) continue;

	// 		const marketInitialSharedVersion =
	// 			orders[0].marketInitialSharedVersion;
	// 		const packageId = orders[0].packageId;

	// 		this.cancelOrdersTx({
	// 			tx,
	// 			collateralCoinType,
	// 			accountCapId,
	// 			marketId,
	// 			marketInitialSharedVersion,
	// 			packageId,
	// 			orderIds: orders.map((order) => order.orderId),
	// 		});

	// 		const netCollateralChange = Helpers.sumBigInt(
	// 			orders.map((order) => order.collateralChange)
	// 		);
	// 		if (netCollateralChange < BigInt(0)) {
	// 			this.deallocateCollateralTx({
	// 				tx,
	// 				accountCapId,
	// 				collateralCoinType,
	// 				marketId,
	// 				marketInitialSharedVersion,
	// 				packageId,
	// 				amount: Helpers.absBigInt(netCollateralChange),
	// 				basePriceFeedId: orders[0].basePriceFeedId,
	// 				collateralPriceFeedId: orders[0].collateralPriceFeedId,
	// 			});
	// 		} else if (netCollateralChange > BigInt(0)) {
	// 			this.allocateCollateralTx({
	// 				tx,
	// 				accountCapId,
	// 				collateralCoinType,
	// 				marketId,
	// 				marketInitialSharedVersion,
	// 				packageId,
	// 				amount: netCollateralChange,
	// 			});
	// 		} else {
	// 			// no collateral change
	// 		}
	// 	}

	// 	return tx;
	// };

	// public buildTransferCollateralTx = (inputs: {
	// 	walletAddress: SuiAddress;
	// 	collateralCoinType: CoinType;
	// 	fromAccountCapId: ObjectId | TransactionArgument;
	// 	toAccountCapId: ObjectId | TransactionArgument;
	// 	amount: Balance;
	// }): Transaction => {
	// 	const {
	// 		walletAddress,
	// 		collateralCoinType,
	// 		fromAccountCapId,
	// 		toAccountCapId,
	// 		amount,
	// 	} = inputs;

	// 	const tx = new Transaction();
	// 	tx.setSender(walletAddress);

	// 	const coinId = this.withdrawCollateralTx({
	// 		tx,
	// 		collateralCoinType,
	// 		amount,
	// 		accountCapId: fromAccountCapId,
	// 	});
	// 	this.depositCollateralTx({
	// 		tx,
	// 		collateralCoinType,
	// 		coinId,
	// 		accountCapId: toAccountCapId,
	// 	});

	// 	return tx;
	// };

	// public buildAllocateCollateralTx = TransactionsApiHelpers.createBuildTxFunc(
	// 	this.allocateCollateralTx
	// );

	// public buildDeallocateCollateralTx =
	// 	TransactionsApiHelpers.createBuildTxFunc(this.deallocateCollateralTx);

	// public buildReduceOrdersTx = (
	// 	inputs: ApiPerpetualsReduceOrdersBody
	// ): Transaction => {
	// 	const {
	// 		walletAddress,
	// 		collateralChange,
	// 		accountCapId,
	// 		collateralCoinType,
	// 		marketId,
	// 		marketInitialSharedVersion,
	// 		packageId,
	// 		basePriceFeedId,
	// 		collateralPriceFeedId,
	// 		orderIds,
	// 		sizesToSubtract,
	// 	} = inputs;

	// 	const tx = new Transaction();
	// 	tx.setSender(walletAddress);

	// 	this.reduceOrdersTx({
	// 		tx,
	// 		packageId,
	// 		collateralCoinType,
	// 		accountCapId,
	// 		marketId,
	// 		marketInitialSharedVersion,
	// 		orderIds,
	// 		sizesToSubtract,
	// 	});

	// 	if (collateralChange < BigInt(0)) {
	// 		this.deallocateCollateralTx({
	// 			tx,
	// 			accountCapId,
	// 			collateralCoinType,
	// 			marketId,
	// 			marketInitialSharedVersion,
	// 			packageId,
	// 			basePriceFeedId,
	// 			collateralPriceFeedId,
	// 			amount: Helpers.absBigInt(collateralChange),
	// 		});
	// 	} else if (collateralChange > BigInt(0)) {
	// 		this.allocateCollateralTx({
	// 			tx,
	// 			accountCapId,
	// 			collateralCoinType,
	// 			marketId,
	// 			marketInitialSharedVersion,
	// 			packageId,
	// 			amount: collateralChange,
	// 		});
	// 	} else {
	// 		// no collateral change
	// 	}

	// 	return tx;
	// };

	// =========================================================================
	//  Helpers
	// =========================================================================

	public getAccountCapType = (inputs: {
		collateralCoinType: CoinType;
	}): string => {
		return `${this.addresses.packages.events}::${PerpetualsApi.constants.moduleNames.account}::Account<${inputs.collateralCoinType}>`;
	};

	// =========================================================================
	//  Private Helpers
	// =========================================================================

	// private fetchOrdersSizes = async (inputs: {
	// 	orderIds: PerpetualsOrderId[];
	// 	collateralCoinType: ObjectId;
	// 	marketId: PerpetualsMarketId;
	// }): Promise<bigint[]> => {
	// 	const { orderIds, marketId, collateralCoinType } = inputs;
	// 	if (orderIds.length <= 0) return [];

	// 	const tx = new Transaction();

	// 	const orderbookId = this.getOrderbookTx({
	// 		tx,
	// 		collateralCoinType,
	// 		marketId,
	// 	});

	// 	for (const orderId of orderIds) {
	// 		this.getOrderSizeTx({
	// 			tx,
	// 			orderId,
	// 			orderbookId,
	// 		});
	// 	}

	// 	const { allBytes } =
	// 		await this.api.Inspections().fetchAllBytesFromTx({
	// 			tx,
	// 		});

	// 	const sizes = allBytes
	// 		.slice(1)
	// 		.map((bytes) => Casting.bigIntFromBytes(bytes[0]));
	// 	return sizes;
	// };

	// private fetchOrderbookOrders = async (inputs: {
	// 	collateralCoinType: ObjectId;
	// 	marketId: PerpetualsMarketId;
	// 	side: PerpetualsOrderSide;
	// 	fromPrice: PerpetualsOrderPrice;
	// 	toPrice: PerpetualsOrderPrice;
	// }): Promise<PerpetualsOrderInfo[]> => {
	// 	const { collateralCoinType, marketId, side, fromPrice, toPrice } =
	// 		inputs;

	// 	const tx = new Transaction();

	// 	const orderbookId = this.getOrderbookTx({
	// 		tx,
	// 		collateralCoinType,
	// 		marketId,
	// 	});
	// 	this.inspectOrdersTx({ tx, orderbookId, side, fromPrice, toPrice });

	// 	const bytes =
	// 		await this.api.Inspections().fetchFirstBytesFromTxOutput({
	// 			tx,
	// 		});

	// 	const orderInfos: any[] = bcs
	// 		.vector(perpetualsRegistry.OrderInfo)
	// 		.parse(new Uint8Array(bytes));

	// 	return orderInfos.map((orderInfo) =>
	// 		Casting.perpetuals.orderInfoFromRaw(orderInfo)
	// 	);
	// };

	// public fetchExecutionPrice = async (
	// 	inputs: ApiPerpetualsExecutionPriceBody & {
	// 		collateralCoinType: CoinType;
	// 		marketId: PerpetualsMarketId;
	// 	}
	// ): Promise<ApiPerpetualsExecutionPriceResponse> => {
	// 	const {
	// 		// collateral,
	// 		collateralCoinType,
	// 		marketId,
	// 		side,
	// 		size,
	// 		price,
	// 		lotSize,
	// 		basePriceFeedId,
	// 		collateralPriceFeedId,
	// 	} = inputs;
	// 	// TODO: change this
	// 	const collateral = BigInt(1000000000000000);

	// 	// const accountCapId = perpetualsBcsRegistry
	// 	// 	.ser(`Account<${collateralCoinType}>`, {
	// 	// 		id: {
	// 	// 			id: {
	// 	// 				bytes: "0x0000000000000000000000000000000000000000000000000000000000000321",
	// 	// 			},
	// 	// 		},
	// 	// 		accountId: 0,
	// 	// 		collateral,
	// 	// 	})
	// 	// 	.toBytes();

	// const depositCoinBytes = perpetualsRegistry.Coin.serialize({
	// 	id: "0x0000000000000000000000000000000000000000000000000000000000000123",
	// 	balance: {
	// 		value: BigInt(1000000000000000),
	// 	},
	// }).toBytes();

	// 	const walletAddress = InspectionsApiHelpers.constants.devInspectSigner;

	// 	const tx = new Transaction();
	// 	tx.setSender(walletAddress);

	// 	const accountCapId = this.createAccountTx({
	// 		...inputs,
	// 		tx,
	// 	});
	// 	this.depositCollateralTx({
	// 		tx,
	// 		collateralCoinType,
	// 		accountCapId,
	// 		coinBytes: depositCoinBytes,
	// 	});
	// 	const { sessionPotatoId } = this.createTxAndStartSession({
	// 		tx,
	// 		accountCapId,
	// 		collateralCoinType,
	// 		marketId,
	// 		walletAddress,
	// 		basePriceFeedId,
	// 		collateralPriceFeedId,
	// 		collateralChange: collateral,
	// 		hasPosition: false,
	// 	});
	// 	this.placeLimitOrderTx({
	// 		tx,
	// 		side,
	// 		size,
	// 		collateralCoinType,
	// 		sessionPotatoId,
	// 		orderType: PerpetualsOrderType.Standard,
	// 		price:
	// 			price ??
	// 			(side === PerpetualsOrderSide.Bid
	// 				? BigInt("0x7FFFFFFFFFFFFFFF") // 2^63 - 1
	// 				: BigInt(1)),
	// 	});
	// 	this.getHotPotatoFieldsTx({
	// 		tx,
	// 		collateralCoinType,
	// 		sessionPotatoId,
	// 	});
	// 	this.endSessionAndTransferAccount({
	// 		...inputs,
	// 		tx,
	// 		sessionPotatoId,
	// 		walletAddress,
	// 		collateralChange: BigInt(0),
	// 	});

	// 	const { events } =
	// 		await this.api.Inspections().fetchAllBytesFromTx({
	// 			tx,
	// 		});

	// 	const filledTakerEvent = EventsApiHelpers.findCastEventOrUndefined({
	// 		events,
	// 		eventType: this.eventTypes.filledTakerOrder,
	// 		castFunction: Casting.perpetuals.filledTakerOrderEventFromOnChain,
	// 	});

	// 	const sizeNum = lotSize * Math.abs(Number(size));

	// 	if (!filledTakerEvent) {
	// 		return {
	// 			executionPrice: 0,
	// 			sizeFilled: 0,
	// 			sizePosted: sizeNum,
	// 			fills: [],
	// 		};
	// 	}

	// 	const filledOrderEvents =
	// 		Aftermath.helpers.events.findCastEventsOrUndefined({
	// 			events,
	// 			eventType: this.eventTypes.filledTakerOrder,
	// 			castFunction:
	// 				Casting.perpetuals.filledTakerOrderEventFromOnChain,
	// 		});
	// 	const fills: PerpetualsFilledOrderData[] = filledOrderEvents.map(
	// 		(event) => {
	// 			const size = Math.abs(
	// 				Casting.IFixed.numberFromIFixed(event.baseAssetDelta)
	// 			);
	// 			const sizeUsd = Math.abs(
	// 				Casting.IFixed.numberFromIFixed(event.quoteAssetDelta)
	// 			);
	// 			const price = sizeUsd / size;
	// 			return {
	// 				size,
	// 				price,
	// 			};
	// 		}
	// 	);

	// 	const executionPrice = Perpetuals.calcEntryPrice(filledTakerEvent);
	// 	const sizeFilled = Math.abs(
	// 		Casting.IFixed.numberFromIFixed(filledTakerEvent.baseAssetDelta)
	// 	);
	// 	const sizePosted = sizeNum - sizeFilled;

	// 	return {
	// 		executionPrice,
	// 		sizeFilled,
	// 		sizePosted,
	// 		fills,
	// 	};

	// 	// const { fillReceipts, postReceipt } =
	// 	// 	await this.fetchMarketOrderReceipts(inputs);

	// 	// const sizePosted = postReceipt !== undefined ? postReceipt.size : 0;
	// 	// if (fillReceipts.length <= 0)
	// 	// 	return price !== undefined
	// 	// 		? // simulating limit order
	// 	// 		  {
	// 	// 				executionPrice: Perpetuals.orderPriceToPrice({
	// 	// 					orderPrice: price,
	// 	// 					lotSize,
	// 	// 					tickSize,
	// 	// 				}),
	// 	// 				sizeFilled: 0,
	// 	// 				sizePosted: Number(sizePosted),
	// 	// 		  }
	// 	// 		: // simulating market order
	// 	// 		  {
	// 	// 				executionPrice: 0,
	// 	// 				sizeFilled: 0,
	// 	// 				sizePosted: 0,
	// 	// 		  };

	// 	// const sizeFilled = Helpers.sumBigInt(
	// 	// 	fillReceipts.map((receipt) => receipt.size)
	// 	// );

	// 	// const executionPrice = fillReceipts.reduce((acc, receipt) => {
	// 	// 	const orderPrice = PerpetualsOrderUtils.price(
	// 	// 		receipt.orderId,
	// 	// 		inputs.side === PerpetualsOrderSide.Ask
	// 	// 			? PerpetualsOrderSide.Bid
	// 	// 			: PerpetualsOrderSide.Ask
	// 	// 	);
	// 	// 	const orderPriceNum = Perpetuals.orderPriceToPrice({
	// 	// 		orderPrice,
	// 	// 		lotSize,
	// 	// 		tickSize,
	// 	// 	});

	// 	// 	return (
	// 	// 		acc +
	// 	// 		orderPriceNum * (Number(receipt.size) / Number(sizeFilled))
	// 	// 	);
	// 	// }, 0);

	// 	// return {
	// 	// 	executionPrice,
	// 	// 	sizeFilled: Number(sizeFilled),
	// 	// 	sizePosted: Number(sizePosted),
	// 	// };
	// };

	// private createTxAndStartSession = (inputs: {
	// 	tx?: Transaction;
	// 	collateralCoinType: CoinType;
	// 	accountCapId: ObjectId | TransactionArgument;
	// 	marketId: PerpetualsMarketId;
	// 	marketInitialSharedVersion: ObjectVersion;
	// 	basePriceFeedId: ObjectId;
	// 	collateralPriceFeedId: ObjectId;
	// 	walletAddress: SuiAddress;
	// 	collateralChange: Balance;
	// 	hasPosition: boolean;
	// }) => {
	// 	const { collateralChange, walletAddress, hasPosition } = inputs;
	// 	const { tx: inputsTx, ...nonTxInputs } = inputs;

	// 	const tx = inputsTx ?? new Transaction();
	// 	tx.setSender(walletAddress);

	// 	if (!hasPosition) {
	// 		this.createMarketPositionTx({
	// 			...nonTxInputs,
	// 			tx,
	// 		});
	// 	}

	// 	if (collateralChange > BigInt(0)) {
	// 		this.allocateCollateralTx({
	// 			...nonTxInputs,
	// 			tx,
	// 			amount: collateralChange,
	// 		});
	// 	}

	// 	const sessionPotatoId = this.startSessionTx({
	// 		...nonTxInputs,
	// 		tx,
	// 	});

	// 	return { tx, sessionPotatoId };
	// };

	// private endSessionAndShareMarket = (inputs: {
	// 	tx: Transaction;
	// 	collateralCoinType: CoinType;
	// 	sessionPotatoId: ObjectId | TransactionArgument;
	// }) => {
	// 	const marketId = this.endSessionTx(inputs);
	// 	this.shareClearingHouseTx({
	// 		...inputs,
	// 		marketId,
	// 	});
	// };

	// =========================================================================
	//  Public Static Helpers
	// =========================================================================

	// public static bucketOrders = (inputs: {
	// 	orders: PerpetualsOrderInfo[];
	// 	side: PerpetualsOrderSide;
	// 	lotSize: number;
	// 	tickSize: number;
	// 	priceBucketSize: number;
	// 	initialBucketedOrders?: OrderbookDataPoint[];
	// }): OrderbookDataPoint[] => {
	// 	const {
	// 		orders,
	// 		side,
	// 		lotSize,
	// 		tickSize,
	// 		priceBucketSize,
	// 		initialBucketedOrders,
	// 	} = inputs;

	// 	let dataPoints: OrderbookDataPoint[] = initialBucketedOrders ?? [];

	// 	const roundPrice = (price: number, bucketSize: number): number => {
	// 		return Math.round(price / bucketSize) * bucketSize;
	// 	};

	// 	const comparePrices = (
	// 		price1: number,
	// 		price2: number,
	// 		bucketSize: number
	// 	): boolean => {
	// 		return Math.abs(price1 - price2) < bucketSize;
	// 	};

	// 	orders.forEach((order) => {
	// 		const actualPrice = Perpetuals.orderPriceToPrice({
	// 			lotSize,
	// 			tickSize: Math.abs(tickSize),
	// 			orderPrice: order.price,
	// 		});
	// 		const roundedPrice = roundPrice(actualPrice, priceBucketSize);
	// 		const size =
	// 			lotSize *
	// 			Math.abs(Number(order.size)) *
	// 			(tickSize < 0 ? -1 : 1);
	// 		const sizeUsd = size * actualPrice;

	// 		const placementIndex = dataPoints.findIndex(
	// 			(dataPoint: OrderbookDataPoint) =>
	// 				comparePrices(
	// 					roundedPrice,
	// 					dataPoint.price,
	// 					priceBucketSize
	// 				)
	// 		);

	// 		if (placementIndex < 0) {
	// 			if (size > 0) {
	// 				const newDataPoint: OrderbookDataPoint = {
	// 					size,
	// 					sizeUsd,
	// 					totalSize: size,
	// 					totalSizeUsd: sizeUsd,
	// 					price: roundedPrice,
	// 				};
	// 				const insertIndex = dataPoints.findIndex((dataPoint) =>
	// 					side === PerpetualsOrderSide.Ask
	// 						? roundedPrice < dataPoint.price
	// 						: roundedPrice > dataPoint.price
	// 				);
	// 				if (insertIndex < 0) {
	// 					dataPoints.push(newDataPoint);
	// 				} else {
	// 					dataPoints.splice(insertIndex, 0, newDataPoint);
	// 				}
	// 			}
	// 		} else {
	// 			dataPoints[placementIndex].size += size;
	// 			dataPoints[placementIndex].sizeUsd += sizeUsd;
	// 			dataPoints[placementIndex].totalSize += size;
	// 			dataPoints[placementIndex].totalSizeUsd += sizeUsd;
	// 		}
	// 	});

	// 	dataPoints = dataPoints.filter((data) => data.size >= lotSize);

	// 	for (let index = 0; index < dataPoints.length; index++) {
	// 		if (index > 0) {
	// 			dataPoints[index].totalSize =
	// 				dataPoints[index - 1].totalSize + dataPoints[index].size;
	// 			dataPoints[index].totalSizeUsd =
	// 				dataPoints[index - 1].totalSizeUsd +
	// 				dataPoints[index].sizeUsd;
	// 		} else {
	// 			dataPoints[index].totalSize = dataPoints[index].size;
	// 			dataPoints[index].totalSizeUsd = dataPoints[index].sizeUsd;
	// 		}
	// 	}

	// 	if (side === PerpetualsOrderSide.Ask) {
	// 		dataPoints.reverse();
	// 	}
	// 	return dataPoints;
	// };

	// =========================================================================
	//  Event Types
	// =========================================================================

	private readonly eventType = (eventName: string) =>
		EventsApiHelpers.createEventType(
			this.addresses.packages.events,
			PerpetualsApi.constants.moduleNames.events,
			eventName
		);

	// =========================================================================
	//  Object Types
	// =========================================================================

	// private marketObjectType = (inputs: { collateralCoinType: CoinType }) =>
	// 	`${
	// 		inputs.packageId
	// 	}::clearing_house::ClearingHouse<${Helpers.addLeadingZeroesToType(
	// 		inputs.collateralCoinType
	// 	)}>`;
}
